import WeekPickerSundayStart from "./WeekPickerSundayStart";
import { Button, UncontrolledTooltip  } from "reactstrap";
import moment from "moment";
import ScheduleBlocks from "./ScheduleBlocks";
import React, {useEffect, useState} from 'react';

const ADJUSTMENT_STATUSES = [1, 2, 4];

const adjustmentStatusWord = (status) => {
	if (status === 1) return 'pending';
	return 'approved';
};

const timesEqual = (a, b) => {
	const left = (a || []).slice().sort().join(',');
	const right = (b || []).slice().sort().join(',');
	return left === right;
};

export default function ScheduleComponent({
	tors = [],
	daysOff = [],
	disabled = false,
	initialData,
	blocks,
	returnSelected,
	isEngineer = false,
	defaultDate = null,
}) {
	const [tempWorkingDays, setTempWorkingDays] = useState(initialData); // { day: string, times: [] }

	useEffect(() => {
		if (typeof returnSelected === 'function') {
			returnSelected(tempWorkingDays);
		}
	}, [tempWorkingDays]);

	useEffect(() => {
		setTempWorkingDays(initialData)
	}, [defaultDate, initialData]);

	const getAdjustmentsForDayDate = (dayDate) => {
		return tors.filter((tor) => {
			if (!ADJUSTMENT_STATUSES.includes(tor.Status)) {
				return false;
			}
			const start = moment(tor.RequestedDateStart).startOf('day');
			const end = moment(tor.RequestedDateEnd || tor.RequestedDateStart).startOf('day');
			return dayDate.isBetween(start, end, 'day', '[]');
		});
	};

	const getOfficeAvailableHalves = (dayDate) => {
		const adjustments = getAdjustmentsForDayDate(dayDate);
		if (adjustments.some((adj) => adj.FullDay)) {
			return [];
		}
		let available = [...blocks];
		adjustments.forEach((adj) => {
			if (adj.FullDay) {
				return;
			}
			if (adj.FullDayAmPm) {
				available = available.filter((h) => h !== 'AM');
			} else {
				available = available.filter((h) => h !== 'PM');
			}
		});
		return available;
	};

	const getOfficeHalfLock = (dayDate, half) => {
		const adjustments = getAdjustmentsForDayDate(dayDate);
		for (const adj of adjustments) {
			if (adj.FullDay) {
				return {
					locked: true,
					reason: `This day is blocked due to an ${adjustmentStatusWord(adj.Status)} Schedule Adjustment (full day).`,
				};
			}
			const offAm = !!adj.FullDayAmPm;
			if ((half === 'AM' && offAm) || (half === 'PM' && !offAm)) {
				const halfLabel = half === 'AM' ? 'morning (AM)' : 'afternoon (PM)';
				return {
					locked: true,
					reason: `The ${halfLabel} is blocked due to an ${adjustmentStatusWord(adj.Status)} Schedule Adjustment.`,
				};
			}
		}
		return { locked: false, reason: null };
	};

	const handleTempWorkingDays = (e) => {
		const day = e.currentTarget.id;
		const dayIndex = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].indexOf(day);
		const dayDate = moment(defaultDate).add(dayIndex, 'days');

		setTempWorkingDays((prev) => {
			const exists = prev.find(d => d.day === day);

			if (exists) {
				return prev.filter(d => d.day !== day);
			}

			if (isEngineer) {
				return [...prev, { day, times: blocks }];
			}

			const initial = initialData.find(d => d.day === day);
			if (!initial) {
				return prev;
			}
			const available = getOfficeAvailableHalves(dayDate);
			if (available.length === 0) {
				return prev;
			}
			return [...prev, {
				day,
				times: [...available],
				block: initial?.block,
			}];
		});
	};

	const updateDayBlocks = (day, updatedTimes) => {
		setTempWorkingDays((prev) => {
			const entry = prev.find((d) => d.day === day);
			if (updatedTimes.length === 0) {
				if (!entry) {
					return prev;
				}
				return prev.filter(d => d.day !== day);
			}
			if (entry && timesEqual(entry.times, updatedTimes)) {
				return prev;
			}
			return prev.map((e) =>
				e.day === day ? { ...e, times: updatedTimes } : e
			);
		});
	};

	const getTorForDayDate = (dayDate) => {
		return getAdjustmentsForDayDate(dayDate)[0];
	};

	return (
		<div className="form-group">
			<div style={{display: 'flex', flexDirection: 'column'}}>
				<b style={{textAlign: 'center', alignSelf: 'center', fontSize: 16}}>Independent Contractor Time Select</b>
				<p style={{textAlign: 'center', alignSelf: 'center', marginBottom: 0}}>Set the times you will work for the selected week:</p>
			</div>
			{defaultDate && (
				<div className="row" key={tempWorkingDays?.length + '_id'}>
					<div
						style={{
							display: 'flex',
							flexWrap: 'wrap',
							justifyContent: 'center',
							gap: '12px',
							width: '100%',
						}}
					>
						{['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => {
							const workingDay = tempWorkingDays.find(d => d.day === day);
							const isInitial = initialData.find(d => d.day === day);
							const dayDate = moment(defaultDate).add(index, 'days');
							const dayTor = getTorForDayDate(dayDate);
							const torPartial = dayTor && !dayTor.FullDay;
							const torFullDay = dayTor && !!dayTor.FullDay;
							const isRegularOff = daysOff.includes(day);

							const officeAvailableHalves = !isEngineer ? getOfficeAvailableHalves(dayDate) : [];
							const officeFullDayBlocked = !isEngineer && officeAvailableHalves.length === 0
								&& getAdjustmentsForDayDate(dayDate).some((adj) => adj.FullDay);

							const isBlocked = isEngineer
								? (isRegularOff || torFullDay || torPartial)
								: (isRegularOff || officeFullDayBlocked);

							const dayButtonDisabled = !isInitial || disabled || isBlocked;

							const tooltipId = day;

							const officeHalfLocks = !isEngineer ? {
								AM: getOfficeHalfLock(dayDate, 'AM'),
								PM: getOfficeHalfLock(dayDate, 'PM'),
							} : null;

							const officeSelectableHalves = !isEngineer
								? officeAvailableHalves
								: null;

							return (
								<div
									key={day}
									style={{
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'center',
										flex: '1 1 120px',
										minWidth: '120px',
										maxWidth: '160px',
										boxSizing: 'border-box',
									}}
								>
									<Button
										id={day}
										onClick={handleTempWorkingDays}
										outline={!workingDay}
										active={!!workingDay}
										disabled={dayButtonDisabled}
										color="primary"
										style={{
											display: 'flex',
											flexDirection: 'column',
											justifyContent: 'center',
											alignItems: 'center',
											padding: '10px 12px',
											width: '100%',
											lineHeight: 1.2,
										}}
									>
										<div style={{ fontWeight: '500' }}>{day}</div>
										<div
											style={{
												fontSize: '0.9em',
												color: workingDay ? 'white' : '#333',
											}}
										>
											{dayDate.format('MMM D')}
										</div>
										{isEngineer && torPartial ?
											<div
												style={{
													fontSize: '0.9em',
													color: workingDay ? 'white' : '#333',
													marginTop: '4px',
													fontStyle: 'italic',
												}}
											>
												Schedule Adjustment – {dayTor.FullDayAmPm ? 'Off AM' : 'Off PM'}
											</div> :
											<div
												style={{
													fontSize: '0.9em',
													color: workingDay ? 'white' : '#333',
													marginTop: '4px',
													fontStyle: 'italic',
												}}
											>
												{!!workingDay ? 'Working' : 'Off'}
											</div>}
									</Button>

									{disabled && <UncontrolledTooltip placement={"top"} target={tooltipId}>
										This schedule has already been approved.
									</UncontrolledTooltip>}

									{isEngineer && !disabled && isRegularOff && <UncontrolledTooltip placement={"top"} target={tooltipId}>
										This day is blocked due to your regular schedule.
									</UncontrolledTooltip>}

									{isEngineer && !disabled && !isRegularOff && (torFullDay || torPartial) && <UncontrolledTooltip placement={"top"} target={tooltipId}>
										This day is blocked due to a Schedule Adjustment.
									</UncontrolledTooltip>}

									{!isEngineer && !disabled && isRegularOff && <UncontrolledTooltip placement={"top"} target={tooltipId}>
										This day is blocked due to your regular schedule.
									</UncontrolledTooltip>}

									{!isEngineer && !disabled && !isRegularOff && officeFullDayBlocked && <UncontrolledTooltip placement={"top"} target={tooltipId}>
										{getOfficeHalfLock(dayDate, 'AM').reason || 'This day is blocked due to a Schedule Adjustment.'}
									</UncontrolledTooltip>}

									{workingDay && (
										<div
											style={{
												marginTop: '8px',
												width: '100%',
												height: 'fit-content',
												overflowY: 'auto',
												backgroundColor: '#f3f4f6',
												padding: '4px',
												boxSizing: 'border-box',
											}}
										>
											<ScheduleBlocks
												disabled={disabled || isBlocked}
												blocks={blocks}
												selected={workingDay.times}
												isEngineer={isEngineer}
												returnSelected={(updatedTimes) => updateDayBlocks(day, updatedTimes)}
												initialBlocks={isEngineer
													? (!!isInitial ? isInitial.times : [])
													: officeSelectableHalves}
												blockLabel={(workingDay && workingDay.block) || (isInitial && isInitial.block) || null}
												dayName={day}
												lockedBlocks={!isEngineer
													? blocks.filter((h) => officeHalfLocks[h]?.locked)
													: []}
												lockReasons={!isEngineer ? {
													AM: officeHalfLocks.AM.reason,
													PM: officeHalfLocks.PM.reason,
												} : null}
											/>
										</div>
									)}
								</div>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
}
